import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogActions,
  DialogTitle,
  Tooltip,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import SendIcon from '@material-ui/icons/Send';
import CheckCircleOutline from '@material-ui/icons/CheckCircleOutline';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import { COLORS } from '../../util/Colors';
import { useRootStore } from '../../../stores/BridgeProvider';
import { useSnackBars } from '../../../alerts/SnackBarProvider';
import Spinner from '../../util/Spinner';
import { FlexDiv, StandardBox } from '../../styles/styles';
import { safeEval } from '../../util/UtilFunctions';
import UnauthorizedSnackbar from '../../common/UnauthorizedSnackbar';

const PendingInvites = () => {
  const history = useHistory();
  const { authStore, dataStore, orgStore } = useRootStore();
  const [isLoading, setLoading] = useState(true);
  const [sentInvites, setSentInvites] = useState([]);
  const [receivedInvites, setReceivedInvites] = useState([]);
  const [authorized, setAuthorized] = useState(true);
  const [open, setOpen] = useState(false);
  const handleAlert = useSnackBars();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const fetchInvites = async () => {
    setLoading(true);
    try {
      const { data: returnedSentInvites } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/organization/invitations`,
      );
      setSentInvites(returnedSentInvites);
    } catch (e) {
      console.error(e);
      const { status } = e?.response;
      if (status === 403) setAuthorized(false);
    }
    try {
      const { data: returnedReceivedInvites } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/invitations`,
      );
      setReceivedInvites(returnedReceivedInvites);
    } catch (e) {
      console.error(e);
      const { status } = e?.response;
      if (status === 403) setAuthorized(false);
    }

    setLoading(false);
  };

  const resendInvite = async values => {
    const { Email, Roles, ContactId } = values;
    const { name: inviterName, email: inviterEmail } = authStore;
    const { settings } = orgStore;
    const roleIds = Roles.map(role => role.OrgRoleId);

    const contactName = safeEval(
      () => dataStore.getContactFromId(contactId).Name,
      null,
    );
    try {
      const res = await authStore.request.post(
        `${process.env.REACT_APP_API}/api/invitation`,
        {
          email: Email,
          roles: roleIds,
          contactId: ContactId,
          orgName: settings.Name,
          emailTemplateData: {
            inviter_name: inviterName,
            inviter_email: inviterEmail,
          },
          ...(contactName ? { name: contactName } : {}),
        },
      );
      res.status === 200
        ? handleAlert('Email successfully sent', COLORS.green)
        : handleAlert('Something went wrong', COLORS.red);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchInvites();
  }, []);

  const acceptInvite = async id => {
    try {
      await authStore.request.post(
        `${process.env.REACT_APP_API}/api/organization/accept-invitation`,
        { invitationId: id },
      );
      fetchInvites();
    } catch (e) {
      console.error(e);
    }
  };
  const rejectInvite = async id => {
    try {
      await authStore.request.delete(
        `${process.env.REACT_APP_API}/api/invitation`,
        {
          data: { invitationId: id },
        },
      );
      fetchInvites();
    } catch (e) {
      console.error(e);
    }
  };

  if (!authorized)
    return (
      <UnauthorizedSnackbar
        message="Unauthorized"
        closeLink="/profile/settings"
      />
    );
  if (isLoading) return <Spinner />;
  return (
    <FlexDiv>
      <StandardBox>
        {sentInvites.length === 0 && receivedInvites.length === 0 ? (
          <p style={{ marginTop: '80px' }}>You have no pending invitations</p>
        ) : (
          <div>
            {sentInvites.length > 0 && (
              <List>
                <Typography
                  variant="h4"
                  style={{
                    marginTop: '2rem',
                    borderBottom: 'solid 0.5px #E0E0E0',
                    paddingBottom: '1rem',
                  }}
                >
                  My Sent Invites
                </Typography>
                {sentInvites.map((invitation, i) => {
                  const { Email, InvitationId, Expires } = invitation;
                  return (
                    <ListItem key={InvitationId}>
                      <ListItemText
                        primary={Email}
                        secondary={`Expires: ${format(Expires, 'MM/DD/YYYY')}`}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip
                          title="Resend Invitation"
                          placement="bottom"
                          arrow
                        >
                          <IconButton
                            edge="end"
                            aria-label="resend"
                            onClick={() => resendInvite(invitation)}
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title="Edit Invitation"
                          placement="bottom"
                          arrow
                        >
                          <IconButton
                            edge="end"
                            aria-label="edit"
                            onClick={() =>
                              history.push({
                                pathname: `/invite/${InvitationId}`,
                                state: {
                                  invite: sentInvites[i],
                                },
                              })
                            }
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            )}
            {receivedInvites.length > 0 && (
              <List>
                <Typography
                  variant="h4"
                  style={{
                    marginTop: '1rem',
                    borderTop: 'solid 0.5px #E0E0E0',
                    paddingTop: '1rem',
                  }}
                >
                  My Received Invites
                </Typography>
                {receivedInvites.map(({ Name, InvitationId }) => (
                  <ListItem key={InvitationId}>
                    <ListItemText variant="button" primary={Name} />
                    <ListItemSecondaryAction>
                      <IconButton aria-label="Delete" onClick={handleClickOpen}>
                        <DeleteIcon />
                      </IconButton>
                      <IconButton
                        aria-label="Accept"
                        onClick={() => acceptInvite(InvitationId)}
                      >
                        <CheckCircleOutline />
                      </IconButton>
                      <Dialog
                        open={open}
                        onClose={handleClose}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                      >
                        <DialogTitle id="alert-dialog-title">
                          {`Do you really want to delete?`}
                        </DialogTitle>
                        <DialogActions>
                          <Button onClick={handleClose} color="primary">
                            Cancel
                          </Button>
                          <Button
                            onClick={() => rejectInvite(InvitationId)}
                            color="primary"
                            autoFocus
                          >
                            Delete
                          </Button>
                        </DialogActions>
                      </Dialog>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </div>
        )}
      </StandardBox>
    </FlexDiv>
  );
};

export default PendingInvites;
